import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Text,
    Input,
    HStack,
    VStack,
    Button,
    Spacer,
    Spinner,
    Avatar,
} from '@chakra-ui/react';

import { AddIcon, CheckIcon } from '@chakra-ui/icons';
import { VscRobot } from 'react-icons/vsc';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Const from '../Constants';

import { useTasksStore } from '../Stores/TasksStore';
import { useAgentsStore } from '../Stores/AgentsStore';

import {
  handleUpdateUserConfig,
  getAvatarUrl
} from '../Utils';

import { useEpicsStore } from '../Stores/EpicsStore';

import TaskTopBar from '../Components/TaskTopBar';
import EpicsBar from '../Components/EpicsBar';

const kColumnSpacing = '10px';
const kColumnHeaderHeight = '30px';
const kTaskTextW = '186px';

const TaskCard = ({
  auth,
  task,
  currentTask,
  setCurrentTask,
  dragRef,
  dragStyle,
  dragAttributes,
  dragListeners,
}) => {
  const handleUpdateTask_ = useTasksStore((state) => state.handleUpdateTask_);
  const handleDeleteTask = useTasksStore((state) => state.handleDeleteTask);
  const runningTaskIds = useAgentsStore((state) => state.runningTaskIds);
  const globalAgents = useAgentsStore((state) => state.globalAgents);
  const localAgents = useAgentsStore((state) => state.localAgents);

  const isRunning = runningTaskIds.includes(task.id);
  // Eisenhower "do it now" quadrant: tint the card so it stands out down the
  // column. Dropped once the task is done, so the Done column stays quiet.
  const isCritical =
    !task.done &&
    task.importance === Const.sHigh &&
    task.urgency === Const.sHigh;
  const assignedAgent = task.assigneeType === 'agent'
    ? [...globalAgents, ...localAgents].find((a) => a.id === task.assigneeId)
    : null;

  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(task.title);

  useEffect(() => {
    if (task.title === '') {
      setIsEditing(true);
    }
  }, [task.title]);

  const handleBlur = async () => {
    setIsEditing(false);
    if (title.trim() === '') {
      await handleDeleteTask(auth, task.id);
    } else {
      handleUpdateTask_(auth, task.id, title);
    }
  };

  const handleClick = () => {
    if (setCurrentTask) setCurrentTask(task);
  };

  const handleChange = (e) => {
    setTitle(e.target.value);
  };

  return (
    <HStack
      ref={dragRef}
      style={dragStyle}
      {...(dragAttributes || {})}
      {...(dragListeners || {})}
      onClick={handleClick}
      minW={'240px'}
      maxW={'240px'}
      padding={'12px'}
      border={'1px'}
      borderRadius={'6px'}
      alignItems={'flex-start'}
      borderColor={
        currentTask && currentTask.id === task.id
          ? Const.SelectColor
          : Const.Gray64
      }
      background={isCritical ? Const.CriticalBg : Const.Gray36}
      _hover={{
        background: isCritical ? Const.CriticalBgHover : Const.Gray46,
        cursor: Const.sPointer,
      }}
    >
      <Box
        as={'button'}
        cursor={Const.sPointer}
        borderRadius={'full'}
        border={'1px solid'}
        borderColor={task.done ? Const.DoneColor : Const.Gray165}
        width={'18px'}
        height={'18px'}
        display={'flex'}
        alignItems={Const.sCenter}
        justifyContent={Const.sCenter}
        marginTop={'2px'}
        mr={0}
        _hover={{ borderColor: task.done ? Const.DoneLightColor : Const.Gray100 }}
        onClick={(e) => {
          e.stopPropagation();
          handleUpdateTask_(auth, task.id, null, null, !task.done);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <CheckIcon
          boxSize={2}
          color={task.done ? Const.DoneColor : Const.Gray165}
        />
      </Box>
      {isEditing ? (
        <Input
          autoFocus
          value={title}
          onChange={handleChange}
          onBlur={handleBlur}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(event) => {
            event.stopPropagation(); // keep Space/keys from triggering dnd-kit's KeyboardSensor
            if (event.key === Const.sEnter) {
              handleBlur();
              event.preventDefault();
            }
          }}
        />
      ) : (
        <VStack alignItems={'flex-start'} spacing={1} flex={1} minW={0}>
          <Text
            w={kTaskTextW}
            maxW={kTaskTextW}
            minW={kTaskTextW}
            whiteSpace={'normal'}
            color={task.done ? Const.Gray100 : Const.White}
            align={Const.Left}
          >
            {task.title}
          </Text>
          {task.assigneeType === 'agent' && (
            <HStack spacing={1} margin={1}>
              {assignedAgent?.avatar ? (
                <Avatar
                  src={getAvatarUrl(assignedAgent.avatar)}
                  name={assignedAgent.name || 'Agent'}
                  size={Const.XS}
                  borderWidth={1}
                />
              ) : (
                <Box color={Const.Orange400}><VscRobot size={11} /></Box>
              )}
              <Text fontSize={Const.SM} color={Const.Gray180} noOfLines={1} maxW={'120px'}>
                {assignedAgent?.name || 'Agent'}
              </Text>
              {isRunning && <Spinner size={'xs'} color={Const.Orange400} />}
            </HStack>
          )}
        </VStack>
      )}
    </HStack>
  );
};

const SortableTask = ({ auth, task, currentTask, setCurrentTask }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <TaskCard
      auth={auth}
      task={task}
      currentTask={currentTask}
      setCurrentTask={setCurrentTask}
      dragRef={setNodeRef}
      dragStyle={style}
      dragAttributes={attributes}
      dragListeners={listeners}
    />
  );
};

const Column = ({
  auth,
  title,
  tasks,
  onAddTask,
  currentTask,
  setCurrentTask,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `col:${title}`,
    data: { type: 'column', status: title },
  });

  const ids = tasks ? tasks.map(t => t.id) : [];

  return (
    <VStack
      padding={'10px'}
      spacing={kColumnSpacing}
      align={'start'}
      borderColor={Const.Gray64}
      borderRadius={'8'}
      minW={'280px'}
      minH={'90vh'}
    >
      <HStack padding={'10px'} width={'100%'}>
        <Text
          color={Const.White}
          fontSize={Const.LG}
          fontWeight={Const.Bold}
          align={Const.Left}
          height={kColumnHeaderHeight}
        >
          {title}
        </Text>
        <Spacer />
        <Button
          leftIcon={<AddIcon boxSize={3} />}
          iconSpacing={0}
          size={Const.SM}
          height={'30px'}
          background={Const.Gray31}
          color={Const.Green500}
          _hover={{ background: Const.Gray64 }}
          onClick={() => onAddTask(title)}
        />
      </HStack>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <VStack
          ref={setNodeRef}
          flex={'1'}
          width={'100%'}
          overflowY={'auto'}
          maxH={'calc(100vh - 120px)'}
          padding={'10px'}
          spacing={kColumnSpacing}
          align={'start'}
          background={isOver ? Const.Gray31 : 'transparent'}
          borderRadius={'6px'}
        >
          {tasks && tasks.map((task) => (
            <Box key={task.id} width={'100%'}>
              <SortableTask
                auth={auth}
                task={task}
                currentTask={currentTask}
                setCurrentTask={setCurrentTask}
              />
            </Box>
          ))}
          <Button
            leftIcon={<AddIcon />}
            size={Const.SM}
            minH={'30px'}
            width={'100%'}
            background={Const.Gray31}
            color={Const.Green500}
            _hover={{ background: Const.Gray69 }}
            onClick={() => onAddTask(title)}
          >
            Add task
          </Button>
        </VStack>
      </SortableContext>
    </VStack>
  );
};

const Tasks = ({
  auth,
  appMode,
  setAppMode,
  userConfig,
  setUserConfig,
  currentTask,
  setCurrentTask,
}) => {
  const tasks_ = useTasksStore((state) => state.tasks);
  const filteredTasks_ = useTasksStore((state) => state.filteredTasks);
  const handleAddTask = useTasksStore((state) => state.handleAddTask);
  const moveTask = useTasksStore((state) => state.moveTask);
  const loadTasks = useTasksStore((state) => state.loadTasks);

  const loadEpics = useEpicsStore((state) => state.loadEpics);

  const isFirstRender = useRef(true);

  const [activeTask, setActiveTask] = useState(null);
  // Local copy of the board used only mid-drag, so the destination column can
  // shift its cards to make space as the active card hovers over it. Null when
  // idle, in which case we render straight from the store.
  const [boardTasks, setBoardTasks] = useState(null);

  const columns = [Const.sTodo, Const.sInProgress, Const.sDoneUp];

  const displayTasks = boardTasks ?? filteredTasks_;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    // Re-fetch on entry so async-seeded/edited tasks show without a browser refresh,
    // mirroring how Meetings/Notes load on mount.
    loadTasks(auth);
    loadEpics(auth);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    handleUpdateUserConfig(auth, userConfig, setUserConfig, {
      [Const.sTasks.toLowerCase()]: {
        currentId: currentTask ? currentTask.id : null,
      },
    });
    // eslint-disable-next-line
  }, [currentTask]);

  useEffect(() => {
    Object.keys(tasks_).forEach((key) => {
      const cur = tasks_[key].find(
        (item) => item.id === userConfig.local[Const.sTasks.toLowerCase()].currentId
      );
      if (cur) {
        setCurrentTask(cur);
      }
    });
    // eslint-disable-next-line
  }, [tasks_]);

  // Look up against the live board (the mid-drag copy when present) so the
  // helpers follow a card as onDragOver relocates it between columns.
  const findContainerOfTask = (taskId) => {
    const source = boardTasks ?? filteredTasks_;
    for (const col of columns) {
      if ((source[col] || []).some((t) => t.id === taskId)) return col;
    }
    return null;
  };

  const parseContainerId = (id) => {
    if (typeof id === 'string' && id.startsWith('col:')) return id.slice(4);
    return findContainerOfTask(id);
  };

  const onDragStart = ({ active }) => {
    const fromCol = findContainerOfTask(active.id);
    if (!fromCol) return;
    const t = (filteredTasks_[fromCol] || []).find((x) => x.id === active.id);
    setActiveTask(t || null);
    // Seed the mid-drag copy with a shallow clone of each column array so
    // onDragOver can splice cards between columns without touching the store.
    const copy = {};
    columns.forEach((c) => { copy[c] = [...(filteredTasks_[c] || [])]; });
    setBoardTasks(copy);
  };

  // While hovering a different column, move the active card into it in the
  // local copy so that column's SortableContext opens a gap and animates.
  const onDragOver = ({ active, over }) => {
    if (!over || !boardTasks) return;
    const fromCol = findContainerOfTask(active.id);
    const toCol = parseContainerId(over.id);
    if (!fromCol || !toCol || fromCol === toCol) return;

    setBoardTasks((prev) => {
      if (!prev) return prev;
      const fromList = prev[fromCol] || [];
      const toList = prev[toCol] || [];
      const movingIndex = fromList.findIndex((t) => t.id === active.id);
      if (movingIndex === -1) return prev;
      const moving = fromList[movingIndex];

      const droppedOnColumn =
        typeof over.id === 'string' && over.id.startsWith('col:');
      let insertIndex = droppedOnColumn
        ? toList.length
        : toList.findIndex((t) => t.id === over.id);
      if (insertIndex === -1) insertIndex = toList.length;

      return {
        ...prev,
        [fromCol]: fromList.filter((t) => t.id !== active.id),
        [toCol]: [
          ...toList.slice(0, insertIndex),
          moving,
          ...toList.slice(insertIndex),
        ],
      };
    });
  };

  const onDragEnd = ({ active, over }) => {
    const source = boardTasks ?? filteredTasks_;
    const finish = () => { setActiveTask(null); setBoardTasks(null); };
    if (!over) return finish();

    const toCol = parseContainerId(over.id);
    if (!toCol) return finish();

    // onDragOver has already placed the card in the destination column within
    // the local copy, so the final position is just the card's neighbors after
    // an arrayMove to the hovered index (this also handles same-column
    // direction: dragging down lands after the target, up lands before it).
    const ids = (source[toCol] || []).map((t) => t.id);
    const oldIndex = ids.indexOf(active.id);
    if (oldIndex === -1) return finish();

    const droppedOnColumn =
      typeof over.id === 'string' && over.id.startsWith('col:');
    let overIndex = droppedOnColumn ? ids.length - 1 : ids.indexOf(over.id);
    if (overIndex === -1) overIndex = ids.length - 1;

    const newIds = arrayMove(ids, oldIndex, overIndex);
    const pos = newIds.indexOf(active.id);
    const beforeId = pos > 0 ? newIds[pos - 1] : null;
    const afterId = pos < newIds.length - 1 ? newIds[pos + 1] : null;

    finish();
    moveTask(auth, { taskId: active.id, toStatus: toCol, beforeId, afterId });
  };

  return (
    <Box width={'100%'}>
      <TaskTopBar auth={auth} appMode={appMode} setAppMode={setAppMode} />
      <HStack align='start' spacing={0}>
        <EpicsBar auth={auth} />

        <Box overflowX={'auto'}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => { setActiveTask(null); setBoardTasks(null); }}
          >
            <HStack align='start' padding={'10px'}>
              {columns.map((col) => (
                <Column
                  key={col}
                  auth={auth}
                  title={col}
                  tasks={displayTasks[col]}
                  onAddTask={() => handleAddTask(auth, col)}
                  currentTask={currentTask}
                  setCurrentTask={setCurrentTask}
                />
              ))}
            </HStack>
            <DragOverlay>
              {activeTask ? (
                <TaskCard
                  auth={auth}
                  task={activeTask}
                  currentTask={currentTask}
                  setCurrentTask={null}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>
      </HStack>
    </Box>
  );
};

export default Tasks;
